{
  "meta": {
    "manager": {
      "name": "Casey Whitmore",
      "first_name": "Casey"
    },
    "report": {
      "name": "Riley Tanaka",
      "first_name": "Riley",
      "role": "Marketing Analyst",
      "tenure": "~8 months"
    },
    "period": {
      "start": "2025-07-15",
      "end": "2026-01-27"
    },
    "generated": "2026-03-06",
    "meeting_count": 4,
    "work_summary_count": 5
  },
  "dashboard": [
    {
      "dimension": "Frequency Health",
      "icon": "📅",
      "status": "critical",
      "status_label": "Severe Gaps",
      "detail": "4 meetings in 8 months, avg 58 days apart"
    },
    {
      "dimension": "Blockers & Support",
      "icon": "🚧",
      "status": "critical",
      "status_label": "Neglected",
      "detail": "3 blockers raised, 0 resolved"
    },
    {
      "dimension": "Psychological Safety",
      "icon": "🛡️",
      "status": "critical",
      "status_label": "Eroding",
      "detail": "Concerns raised then ignored repeatedly"
    },
    {
      "dimension": "Feedback & Recognition",
      "icon": "🌟",
      "status": "critical",
      "status_label": "Absent",
      "detail": "Zero feedback or recognition on record"
    },
    {
      "dimension": "Expectations Tracker",
      "icon": "🎯",
      "status": "critical",
      "status_label": "Drifting",
      "detail": "2 goals stale, 1 action item orphaned"
    },
    {
      "dimension": "Meeting Structure",
      "icon": "📋",
      "status": "critical",
      "status_label": "Ineffective",
      "detail": "No agenda, no action tracking, status-only"
    },
    {
      "dimension": "Autonomy & Delegation",
      "icon": "🧭",
      "status": "alert",
      "status_label": "Abandonment",
      "detail": "High autonomy without support or direction"
    },
    {
      "dimension": "Career Growth",
      "icon": "🌱",
      "status": "critical",
      "status_label": "Missing",
      "detail": "A/B testing interest raised, never followed up"
    },
    {
      "dimension": "Decision Making",
      "icon": "⚖️",
      "status": "alert",
      "status_label": "Stalled",
      "detail": "Key decisions deferred or vague"
    },
    {
      "dimension": "Personal Connection",
      "icon": "💬",
      "status": "alert",
      "status_label": "Minimal",
      "detail": "Zero personal touchpoints in any meeting"
    },
    {
      "dimension": "Talk Ratio",
      "icon": "🎙️",
      "status": "alert",
      "status_label": "Manager-Heavy",
      "detail": "Casey directs; Riley executes and asks"
    },
    {
      "dimension": "Energy Alignment",
      "icon": "⚡",
      "status": "alert",
      "status_label": "Unknown",
      "detail": "Interests hinted at but never explored"
    },
    {
      "dimension": "Workload Balance",
      "icon": "⏱️",
      "status": "alert",
      "status_label": "Overloaded",
      "detail": "3 projects assigned with no prioritization"
    },
    {
      "dimension": "Team Dynamics",
      "icon": "👥",
      "status": "alert",
      "status_label": "Isolated",
      "detail": "No collaborators visible beyond Casey"
    }
  ],
  "dimensions": [
    {
      "name": "Frequency Health",
      "icon": "📅",
      "status": "critical",
      "insights": [
        {
          "title": "Only 4 meetings in over 6 months",
          "priority": "high",
          "body": "Riley has had just 4 one-on-ones since starting in July 2025. The gaps between meetings are 42 days, 49 days, and 84 days. For a new hire in their first year, the recommended cadence is weekly. This is the single most foundational issue — almost every other problem in this analysis stems from the lack of regular contact.",
          "evidence": "Meetings on Aug 5, Sep 16, Nov 4, Jan 27. Average interval: 58 days. The gap from Nov 4 to Jan 27 is nearly 3 months.",
          "action": {
            "label": "Open Next Meeting With",
            "text": "Riley, I owe you an apology. We've only met four times in the six months you've been here, and that's completely on me. I'm committing to weekly 30-minute one-on-ones starting this week. I want to protect this time for you — it's not optional for me anymore. What day and time works best?",
            "variant": "default"
          }
        },
        {
          "title": "Meeting cut short due to manager scheduling conflict",
          "priority": "high",
          "body": "The Nov 4 meeting was reduced to 15 minutes because of Casey's schedule, turning an already rare meeting into a cursory check-in. When meetings are infrequent, each one carries outsized importance. Cutting them short sends a signal that Riley's time is less important.",
          "evidence": "Nov 4 meeting summary: 'Meeting was cut short to 15 minutes due to Casey's scheduling conflict.'",
          "action": {
            "label": "Manager Action",
            "text": "Block your 1-on-1 with Riley as a recurring calendar event with highest priority. If a conflict arises, reschedule the 1-on-1 to the same week rather than skipping it entirely. Never cancel — only move.",
            "variant": "default"
          }
        }
      ]
    },
    {
      "name": "Blockers & Support",
      "icon": "🚧",
      "status": "critical",
      "insights": [
        {
          "title": "A/B testing access never provided",
          "priority": "high",
          "body": "Riley identified needing A/B testing tool access as early as late July in their work summary. They mentioned wanting to learn A/B testing in the Sep 16 meeting. Their goal check-in explicitly requested 'guidance on A/B testing tools.' None of these requests were addressed. This blocker directly prevented progress on the email open rate goal.",
          "evidence": "Work summary Aug 3: 'need access to A/B testing feature.' Sep 16 meeting: 'Riley mentioned wanting to learn A/B testing but the conversation moved on.' Goal check-in Sep 16: 'Need guidance on A/B testing tools.'",
          "action": {
            "label": "Suggested Discussion Topic",
            "text": "Riley, you've asked about A/B testing tools multiple times and I dropped the ball every time. I'm sorry. Let's fix this right now — what tool access do you need, and I'll make sure you have it by end of this week.",
            "variant": "default"
          }
        },
        {
          "title": "Year-end report template request ignored twice",
          "priority": "high",
          "body": "Riley asked for a template or example for the year-end report via Slack at least twice and never received a response. The report became overdue, and in the Jan 27 meeting Casey expressed frustration — even though the root cause was Casey's own unresponsiveness. This is a trust-damaging pattern: the report asks for help, gets silence, falls behind, then feels blamed.",
          "evidence": "Riley's note Jan 27: 'I asked for a template or example twice in Slack and never got a response. Now I'm behind and it feels like it's my fault.' Work summary Nov 23: 'messaged Casey on Slack but haven't heard back yet.'",
          "action": {
            "label": "Suggested Discussion Topic",
            "text": "Riley, I want to acknowledge something. You reached out to me multiple times on Slack about the year-end report and I didn't respond. That put you in an impossible position. Going forward, if I don't respond to a Slack message within 24 hours, I want you to ping me again or flag it as urgent. I won't be annoyed — I'll be grateful.",
            "variant": "default"
          }
        },
        {
          "title": "Landing page feedback delivered too late",
          "priority": "medium",
          "body": "Riley submitted landing page copy on Monday and didn't receive feedback until Friday afternoon, forcing a rushed publish. Riley noted the hero copy wasn't strong enough but didn't get a review cycle. This pattern of delayed feedback is blocking Riley's ability to do quality work and learn from iterations.",
          "evidence": "Work summary Aug 17: 'Submitted for review on Monday. Didn't receive feedback until Friday afternoon. Made requested changes and published same day.' Riley's note Sep 16: 'I didn't get feedback before it went live.'",
          "action": {
            "label": "Manager Action",
            "text": "Commit to a 48-hour turnaround on reviews for Riley's work. If you can't review something yourself, designate someone else. For Riley's development, the review conversation is more valuable than the review itself.",
            "variant": "default"
          }
        }
      ]
    },
    {
      "name": "Psychological Safety",
      "icon": "🛡️",
      "status": "critical",
      "insights": [
        {
          "title": "Raise-respond cycle is broken — concerns go unacknowledged",
          "priority": "high",
          "body": "Riley has shown vulnerability multiple times: expressing desire for feedback on the landing page, noting they feel like they're 'flying blind,' and flagging that unanswered Slack messages made them feel blamed. In every case, there is no evidence Casey acknowledged these concerns or took action. This is actively destroying psychological safety — Riley is learning that raising issues is pointless.",
          "evidence": "Riley's note Nov 4: 'I feel like I'm flying blind on these projects. I don't know if my work is good or bad — just get told to ship it.' Riley's note Jan 27: 'Now I'm behind and it feels like it's my fault.'",
          "action": {
            "label": "Open Next Meeting With",
            "text": "Riley, I've been reflecting on our last few conversations and I realize I haven't been responsive to the things you've raised. When you told me you felt like you were flying blind, that should have been a wake-up call for me. I want you to know — your feedback about what's not working is the most valuable thing you can give me. I'm going to do better at listening and acting on it.",
            "variant": "default"
          }
        },
        {
          "title": "Blame dynamic emerging around year-end report",
          "priority": "high",
          "body": "The year-end report situation created a dynamic where Riley asked for help, was ignored, missed the deadline, and then faced frustration from Casey. Even though Casey acknowledged the gap in the meeting, Riley's private note reveals they internalized blame. This pattern, if repeated, will shut down all future help-seeking behavior.",
          "evidence": "Jan 27: Casey 'seemed frustrated' about the overdue report. Riley's note: 'it feels like it's my fault.'",
          "action": {
            "label": "Suggested Discussion Topic",
            "text": "Riley, I want to be really clear about the year-end report situation: the miss was on me, not you. You asked for what you needed and I didn't deliver. I don't want you to feel like you're in trouble for something I caused. If you ever feel that way again, please call it out — I can take it.",
            "variant": "default"
          }
        },
        {
          "title": "Riley is still trying — but the window is closing",
          "priority": "medium",
          "body": "Despite the negative experiences, Riley continues to raise issues and share honest notes. This shows residual trust and willingness to engage. However, each ignored concern erodes this further. There is a limited window to repair this relationship before Riley stops sharing altogether and either quietly disengages or starts looking elsewhere.",
          "evidence": "Riley has written candid individual notes in 3 of 4 meetings, showing they still believe sharing might help. This is a positive signal that must be protected.",
          "action": {
            "label": "Manager Action",
            "text": "In your next meeting, explicitly thank Riley for their candor. Say: 'I appreciate that you've been honest with me about what's not working. That takes courage and I don't take it for granted.' Then prove it by acting on one thing they've raised.",
            "variant": "default"
          }
        }
      ]
    },
    {
      "name": "Feedback & Recognition",
      "icon": "🌟",
      "status": "critical",
      "insights": [
        {
          "title": "Zero positive feedback or recognition in 8 months",
          "priority": "high",
          "body": "There is no recorded instance of Casey giving Riley positive feedback, specific recognition, or praise of any kind. The closest thing to a quality signal was 'looks fine' on the holiday campaign — which is not recognition, it's dismissal. Riley has shipped multiple campaigns, improved open rates from Q3 to Q4, researched competitors, built a content calendar, and compiled metrics. None of this has been acknowledged.",
          "evidence": "Nov 4: Casey said the holiday campaign draft 'looks fine' and to ship it. No other positive or specific feedback found in any meeting or data source.",
          "action": {
            "label": "Suggested Discussion Topic",
            "text": "Riley, I want to call out some things I should have recognized sooner. The holiday campaign emails you shipped had a 19.5% open rate — that's a real improvement over Q3's 18%. The competitive research you did for Q4 planning was thorough — 8 campaigns analyzed, 12 touchpoints mapped out. That's strong analytical work and I should have told you that at the time. I'm going to be much more intentional about giving you feedback going forward.",
            "variant": "green"
          }
        },
        {
          "title": "No constructive feedback either — Riley can't calibrate",
          "priority": "high",
          "body": "The absence of feedback isn't just about recognition — it's also about growth. Riley explicitly said they feel like they don't know if their work is good or bad. Without constructive feedback, Riley can't improve. Without positive feedback, they can't build confidence. This is a development vacuum.",
          "evidence": "Riley's note Nov 4: 'I don't know if my work is good or bad — just get told to ship it.'",
          "action": {
            "label": "Manager Action",
            "text": "Starting immediately, give Riley specific feedback on every piece of work they produce. Use the SBI framework: 'On the holiday campaign email (situation), the subject line you wrote drove a 19.5% open rate (behavior), which moved us closer to our target and showed your audience instincts are developing (impact).' Even two sentences makes a difference.",
            "variant": "default"
          }
        }
      ]
    },
    {
      "name": "Expectations Tracker",
      "icon": "🎯",
      "status": "critical",
      "insights": [
        {
          "title": "Email open rate goal at 15% progress with deadline passed",
          "priority": "high",
          "body": "The goal to improve email open rates to 22% had a Dec 31 deadline and is sitting at 15% progress. The only check-in was in September when Riley flagged needing A/B testing tool access — which was never provided. This goal was set and abandoned. Riley was set up to fail by not receiving the tools or guidance needed to move the needle.",
          "evidence": "Goal check-in Sep 16: progress moved to 15%, note says 'Need guidance on A/B testing tools.' No subsequent check-ins. Due date Dec 31 has passed.",
          "action": {
            "label": "Suggested Discussion Topic",
            "text": "Riley, I want to revisit the email open rate goal honestly. The target was 22% by year-end and we didn't get there — but I also didn't give you the A/B testing tools you asked for, which was the key lever. That's on me. Let's reset this goal together: what do you think is a realistic target for Q1, and what do you need from me to get there?",
            "variant": "default"
          }
        },
        {
          "title": "Holiday campaign goal status unclear at 30%",
          "priority": "medium",
          "body": "The Q4 holiday campaign goal shows 30% progress despite the campaign having launched and emails being sent. The goal may not have been updated, or the 'optimize' portion was never addressed. The campaign's due date (Dec 20) has passed without a retrospective or closure.",
          "evidence": "Goal progress: 30%. Campaign emails were shipped in November per work summaries. Due date Dec 20 has passed. No goal update recorded after initial creation.",
          "action": {
            "label": "Suggested Discussion Topic",
            "text": "Riley, let's close out the holiday campaign goal properly. You shipped the campaign and got a 19.5% open rate — that's a real result. What went well? What would you do differently? Let's capture those lessons so your next campaign is even stronger.",
            "variant": "default"
          }
        },
        {
          "title": "Orphaned action item: 'figure out what's going wrong' with email rates",
          "priority": "medium",
          "body": "In the Aug 5 meeting, Casey told Riley to 'figure out what's going wrong' with email open rates by next week. There was no follow-up meeting for 42 days. This vague directive with no support and no follow-up is a textbook orphaned action item that erodes accountability and trust.",
          "evidence": "Aug 5 meeting: Casey asked Riley to 'figure out what's going wrong' by next week. Next meeting wasn't until Sep 16.",
          "action": {
            "label": "Manager Action",
            "text": "Never assign a vague directive like 'figure out what's going wrong' without defining what success looks like, offering resources, and following up. Instead try: 'Can you pull the open rate data by segment and subject line, and come to our next meeting with 2-3 hypotheses? I can help you think through them.'",
            "variant": "default"
          }
        }
      ]
    },
    {
      "name": "Meeting Structure",
      "icon": "📋",
      "status": "critical",
      "insights": [
        {
          "title": "Meetings are status updates with no structure",
          "priority": "high",
          "body": "All four meetings follow the same pattern: Riley shares status, Casey reacts briefly, tasks are assigned. There is no evidence of agendas, action item tracking, career discussion, feedback exchange, or personal check-ins. The meetings are purely transactional and miss the core purpose of a 1-on-1: developing the person, not tracking the work.",
          "evidence": "All meeting summaries describe status updates and task assignments. No highlights recorded in any meeting. Topic categories are exclusively work status and task delegation.",
          "action": {
            "label": "Suggested Discussion Topic",
            "text": "Riley, I want to restructure our one-on-ones so they're more useful for you. Going forward, I'd like you to own the agenda — bring whatever's most on your mind. We'll spend time on your development, not just status updates. Status can go in Slack. Sound good?",
            "variant": "default"
          }
        },
        {
          "title": "No action items tracked or followed up",
          "priority": "high",
          "body": "Across four meetings, no action items were formally captured or revisited. Commitments like 'figure out what's going wrong' and 'we'll figure it out later' float without accountability. When the Jan 27 meeting happened after a 3-month gap, the year-end report was overdue with no one having tracked its progress.",
          "evidence": "Aug 5: 'figure out what's going wrong by next week' — no follow-up for 42 days. Nov 4: year-end report timeline deferred with 'we'll figure it out later' — next meeting was 84 days later with report overdue.",
          "action": {
            "label": "Manager Action",
            "text": "End every 1-on-1 with 2 minutes of action items: 'Here's what I'm committing to do by next week, and here's what you're committing to. I'll put them in writing.' Review them at the start of the next meeting. This single habit will transform your meeting effectiveness.",
            "variant": "default"
          }
        }
      ]
    },
    {
      "name": "Autonomy & Delegation",
      "icon": "🧭",
      "status": "alert",
      "insights": [
        {
          "title": "Abandonment pattern, not delegation",
          "priority": "high",
          "body": "Riley is 8 months into their career at Nimbus as a Marketing Analyst. At this stage, situational leadership calls for S2 (Coaching): explain the why, invite input, guide the process. Instead, Riley is experiencing S4 (Delegating) without the trust and clarity that S4 requires. Projects are assigned with minimal direction, feedback is absent, and requests for guidance go unanswered. This isn't empowerment — it's abandonment.",
          "evidence": "Sep 16: Three Q4 projects assigned 'without discussing bandwidth or prioritization.' Nov 4: Holiday campaign reviewed with 'looks fine, ship it.' Jan 27: Year-end report lacked format guidance despite repeated requests.",
          "action": {
            "label": "Suggested Discussion Topic",
            "text": "Riley, I think I've been too hands-off in a way that hasn't been helpful. My intention was to give you ownership, but I realize I did that without giving you enough context, examples, or feedback to work with. That's not fair to you. For the next few projects, I want to be more involved upfront — setting clearer briefs, reviewing drafts together, and giving you real feedback. As you get more comfortable, we can dial that back. Does that sound right to you?",
            "variant": "default"
          }
        },
        {
          "title": "Riley shows initiative despite lack of support",
          "priority": "positive",
          "body": "Despite the abandonment pattern, Riley has shown real initiative: researching 8 competitor campaigns, building a 12-touchpoint content calendar, proactively segmenting audiences, and attempting subject line variations. These are signs of someone who wants to succeed and is trying to self-direct. This initiative should be explicitly recognized and channeled with proper support.",
          "evidence": "Work summary Oct 12: 'Researched 8 competitor holiday campaigns. Drafted initial content calendar with 12 touchpoints.' Work summary Aug 3: 'Created 3 audience segments based on engagement history.'",
          "action": {
            "label": "Keep Doing",
            "text": "Riley's self-direction is a strength. Recognize it explicitly: 'The competitive research and content calendar you built on your own showed great initiative. I want to give you more of that kind of ownership — with better support from me so you can do even more with it.'",
            "variant": "green"
          }
        }
      ]
    },
    {
      "name": "Career Growth",
      "icon": "🌱",
      "status": "critical",
      "insights": [
        {
          "title": "A/B testing interest raised and dropped",
          "priority": "high",
          "body": "Riley expressed interest in learning A/B testing at least three times across meetings and work summaries. This is a clear growth aspiration that directly aligns with their goal (improving email open rates). It was never explored, supported, or even acknowledged in a meaningful way. This is a broken career development promise — even if the promise was never formally made, ignoring a repeated aspiration sends the message that growth doesn't matter here.",
          "evidence": "Work summary Aug 3: 'Drafted initial subject lines for A/B testing (not yet run — need access).' Sep 16 meeting: 'Riley mentioned wanting to learn A/B testing but the conversation moved on.' Goal check-in Sep 16: 'Need guidance on A/B testing tools.'",
          "action": {
            "label": "Suggested Discussion Topic",
            "text": "Riley, you've brought up A/B testing multiple times and I never followed through. I want to fix that. Let's make A/B testing proficiency an explicit development goal for you this quarter. What would be most helpful — a course, a tool walkthrough, pairing with someone who does this well, or all of the above?",
            "variant": "default"
          }
        },
        {
          "title": "Zero career conversations in 8 months",
          "priority": "high",
          "body": "There has been no discussion of Riley's career aspirations, long-term goals, development plan, or growth trajectory. For someone in their first year, this is a critical gap. Riley doesn't know what 'good' looks like, what promotion criteria are, or what skills to develop. They're executing tasks without a development framework.",
          "evidence": "No meeting highlights of type 'career.' Jan 27 role discussion ended with 'we'll see' — the opposite of career clarity.",
          "action": {
            "label": "Suggested Discussion Topic",
            "text": "Riley, I realize we've never had a real conversation about your career goals and where you want to go. That's a gap I want to fill. What drew you to marketing analytics? Where do you see yourself in a year or two? And what skills do you most want to develop? I want to make sure the work you're doing here is building toward something meaningful for you.",
            "variant": "default"
          }
        }
      ]
    },
    {
      "name": "Decision Making",
      "icon": "⚖️",
      "status": "alert",
      "insights": [
        {
          "title": "Decisions deferred with vague language, never resolved",
          "priority": "high",
          "body": "Multiple important topics were deferred without clear resolution. The year-end report timeline was punted with 'we'll figure it out later.' The team restructure impact on Riley's role was met with 'we'll see.' These deferrals aren't decisions — they're avoidance that creates anxiety and stalls work. Riley is left in ambiguity on issues that directly affect their day-to-day and future.",
          "evidence": "Nov 4: Year-end report timeline — 'we'll figure it out later.' Jan 27: Role restructure — 'we'll see.' Both remain unresolved.",
          "action": {
            "label": "Suggested Discussion Topic",
            "text": "Riley, I know I've left some things vague — especially around the team restructure and your role. You deserve more clarity than 'we'll see.' Here's what I know right now, here's what I don't know yet, and here's when I'll have more information. Can we agree that I'll give you an update by a specific date even if the answer is still uncertain?",
            "variant": "default"
          }
        },
        {
          "title": "Q4 priorities assigned without collaborative decision-making",
          "priority": "medium",
          "body": "In the Sep 16 meeting, Casey outlined Q4 priorities and assigned all three projects to Riley without discussing bandwidth, priority order, or trade-offs. Effective decision-making, especially at the coaching stage, should involve the report in scoping and prioritization. This ensures buy-in and surfaces capacity issues before they become missed deadlines.",
          "evidence": "Sep 16: 'Casey outlined Q4 priorities: holiday campaign, year-end report, and blog content calendar. Assigned all three to Riley without discussing bandwidth or prioritization.'",
          "action": {
            "label": "Manager Action",
            "text": "When assigning multiple projects, involve Riley in the prioritization: 'Here are three things that need to happen in Q4. If you could only do two well, which two would you pick and why? And what would you need from me to do all three?' This builds judgment and surfaces overload early.",
            "variant": "default"
          }
        }
      ]
    },
    {
      "name": "Personal Connection",
      "icon": "💬",
      "status": "alert",
      "insights": [
        {
          "title": "Zero personal touchpoints across all meetings",
          "priority": "high",
          "body": "There is no evidence of any personal conversation in any of the four meetings — no check-in on how Riley is doing as a person, no discussion of life outside work, no rapport-building beyond task assignments. For a new hire, this is particularly damaging. Riley likely feels like a task-execution resource rather than a valued team member.",
          "evidence": "All four meeting summaries and topic discussions are exclusively about work deliverables. No personal content, mood data, or rapport-building observed.",
          "action": {
            "label": "Open Next Meeting With",
            "text": "Riley, before we get into anything work-related — how are you doing? Not work stuff, just... you. How are you settling into the team, how's life going? I want to make sure our conversations aren't just about deliverables.",
            "variant": "default"
          }
        },
        {
          "title": "No mood tracking makes it hard to spot trends",
          "priority": "medium",
          "body": "Without mood data or personal check-ins, there's no way to detect whether Riley is burning out, frustrated, or disengaging — until it's too late. Riley's private notes suggest increasing frustration (from neutral in Aug, to 'flying blind' in Nov, to 'feels like it's my fault' in Jan), but this trend was never surfaced in conversation.",
          "evidence": "Riley's notes show an emotional trajectory: Aug (neutral) → Sep (wanting more support) → Nov ('flying blind') → Jan ('feels like it's my fault'). None of this was addressed in meetings.",
          "action": {
            "label": "Manager Action",
            "text": "Start every 1-on-1 with a genuine 'How are you feeling this week — energy level, motivation, anything weighing on you?' Take note of the answer. Over time, you'll see patterns that help you intervene before frustration becomes disengagement.",
            "variant": "default"
          }
        }
      ]
    },
    {
      "name": "Talk Ratio",
      "icon": "🎙️",
      "status": "alert",
      "insights": [
        {
          "title": "Manager directs, report receives",
          "priority": "medium",
          "body": "While no explicit talk ratio data exists, the meeting notes strongly suggest a manager-driven dynamic. Casey outlines priorities, assigns work, and makes assessments ('looks fine,' 'figure out what's going wrong'). Riley's contributions are mostly status updates and questions. The 1-on-1s read as top-down briefings rather than collaborative conversations where Riley drives the agenda.",
          "evidence": "Aug 5: Casey asked Riley to 'figure out what's going wrong.' Sep 16: Casey outlined and assigned Q4 priorities. Nov 4: Casey said 'looks fine, ship it.' Jan 27: Casey expressed frustration about timeline. Riley's contributions are primarily responsive.",
          "action": {
            "label": "Suggested Discussion Topic",
            "text": "Riley, I want to flip how our 1-on-1s work. From now on, this is your meeting. You set the agenda — bring whatever's most important to you. I'll add a couple of things if I need to, but you go first. What's on your mind this week?",
            "variant": "default"
          }
        },
        {
          "title": "Riley's questions and interests get steamrolled",
          "priority": "medium",
          "body": "When Riley raises topics — A/B testing interest, request for feedback review cycles, year-end report format questions — the conversation moves on without adequate engagement. This trains Riley to stop raising topics, further tilting the ratio toward Casey's agenda.",
          "evidence": "Sep 16: 'Riley mentioned wanting to learn A/B testing but the conversation moved on.' Nov 4: Riley asked about year-end report timeline, got 'we'll figure it out later.'",
          "action": {
            "label": "Manager Action",
            "text": "When Riley raises a topic or question, pause and engage with it fully before moving on. If you genuinely can't address it now, say: 'That's important and I don't want to rush past it. Let's put it at the top of next week's agenda.' Then actually do it.",
            "variant": "default"
          }
        }
      ]
    },
    {
      "name": "Energy Alignment",
      "icon": "⚡",
      "status": "alert",
      "insights": [
        {
          "title": "Analytical and research work appears to energize Riley",
          "priority": "medium",
          "body": "Riley's self-initiated work reveals where their energy lies: competitor research (8 campaigns analyzed), audience segmentation, content calendar design, and data compilation. These are analytical, strategic tasks that Riley gravitates toward without prompting. The energy drain appears to be producing work in a vacuum without feedback or clear direction — not the work itself.",
          "evidence": "Self-initiated: competitor research (Oct), audience segmentation (Aug), content calendar (Oct), metrics compilation (Jan). Energy drain signals from: 'flying blind,' wanting more feedback, needing review cycles.",
          "action": {
            "label": "Suggested Discussion Topic",
            "text": "Riley, what part of your work has felt most engaging since you started? I noticed you dove into the competitor research and content calendar planning on your own — is that the kind of work that energizes you? I want to understand what your ideal work mix looks like so I can steer more of that your way.",
            "variant": "default"
          }
        },
        {
          "title": "Energy alignment is unknown because the question has never been asked",
          "priority": "medium",
          "body": "There has been no conversation about what kind of work Riley finds fulfilling versus draining. Casey is assigning work based on what needs to be done, not on any understanding of Riley's interests or strengths. For someone 8 months in, the manager should have a clear picture of this by now.",
          "evidence": "No meeting includes discussion of Riley's interests, preferred work types, or long-term motivations.",
          "action": {
            "label": "Suggested Discussion Topic",
            "text": "Riley, if you could design your ideal week — the kind of work, the mix of tasks — what would it look like? What would you want more of, and what would you happily hand off to someone else?",
            "variant": "default"
          }
        }
      ]
    },
    {
      "name": "Workload Balance",
      "icon": "⏱️",
      "status": "alert",
      "insights": [
        {
          "title": "Three major projects assigned simultaneously with no prioritization",
          "priority": "high",
          "body": "In September, Casey assigned the holiday campaign, year-end report, and blog content calendar to Riley without discussing capacity or priority order. For a relatively new Marketing Analyst, this is a heavy load — especially without regular check-ins to course-correct. The result was predictable: the year-end report fell behind because there was no clear priority stack.",
          "evidence": "Sep 16: 'Assigned all three to Riley without discussing bandwidth or prioritization.' Year-end report became overdue by Jan 15.",
          "action": {
            "label": "Suggested Discussion Topic",
            "text": "Riley, I want to make sure your plate is manageable and that we're clear on priorities. Let's list everything you're working on right now and stack-rank it together. If something needs to come off the list or get pushed back, that's OK — I'd rather you do two things well than four things halfway.",
            "variant": "default"
          }
        },
        {
          "title": "Idle time from waiting on manager feedback",
          "priority": "medium",
          "body": "Riley's work summaries reveal periods of low productivity caused by waiting on Casey — waiting for landing page feedback (4 days), waiting for Slack responses on the year-end report, waiting for budget and channel input on the holiday campaign. The blocker isn't workload volume, it's flow disruption caused by manager unresponsiveness.",
          "evidence": "Work summary Aug 17: 'Waited on feedback for most of the week.' Nov 23: 'messaged Casey on Slack but haven't heard back yet.' Oct 12: 'Waiting for Casey's input on budget and channel priorities.'",
          "action": {
            "label": "Manager Action",
            "text": "Audit your response patterns. Riley has been blocked multiple times waiting for your input. Set a personal SLA: respond to Riley's requests within 24 hours, even if the response is 'I need until Wednesday to review this.' Unblock your people as your highest priority.",
            "variant": "default"
          }
        }
      ]
    },
    {
      "name": "Team Dynamics",
      "icon": "👥",
      "status": "alert",
      "insights": [
        {
          "title": "Riley appears isolated — Casey is the only visible connection",
          "priority": "high",
          "body": "Across all work summaries and meetings, there is no mention of Riley collaborating with any teammate other than Casey. The only team interaction noted is attending a marketing team standup. For someone 8 months in, this level of isolation is concerning. Riley has no peer reviewer, no collaborator, no mentor — just a mostly-absent manager.",
          "evidence": "Work summaries mention only solo work and interactions with Casey. One mention of 'marketing team standup' attendance (Aug). No peer names appear anywhere in the data.",
          "action": {
            "label": "Suggested Discussion Topic",
            "text": "Riley, who do you work with most closely on the team besides me? Do you feel like you have people you can bounce ideas off of or get input from? I want to make sure you're connected — not just to me, but to the broader team. Is there someone you'd like to collaborate with more?",
            "variant": "default"
          }
        },
        {
          "title": "Team restructure mentioned without context creates anxiety",
          "priority": "medium",
          "body": "Casey mentioned a potential Q2 restructure in the Jan 27 meeting and responded to Riley's question about its impact with 'we'll see.' For someone who's already feeling unsupported and uncertain, an ambiguous restructure signal amplifies anxiety. Even if details aren't finalized, framing and reassurance matter.",
          "evidence": "Jan 27: 'Casey mentioned the team might restructure in Q2. Riley asked what that means for their role and Casey said we'll see.'",
          "action": {
            "label": "Suggested Discussion Topic",
            "text": "Riley, I mentioned a possible restructure last time and I realize I left you hanging. Here's what I can tell you right now: [share what you know]. Here's what I don't know yet: [be honest]. And here's my commitment: I'll keep you informed as things develop, and your input matters in how this shapes up. You're not going to be surprised by this.",
            "variant": "default"
          }
        }
      ]
    }
  ],
  "agenda": {
    "next_meeting": [
      {
        "topic": "Acknowledge and repair the relationship",
        "tags": ["psychological-safety", "personal-connection"],
        "opener": "Riley, I owe you an honest conversation. I've been reflecting on the last several months and I realize I haven't been the manager you deserve. I want to talk about what I'm going to change — but first, I want to hear from you: how are you feeling about things right now? Give it to me straight."
      },
      {
        "topic": "Apologize for dropped blockers — A/B testing and year-end report",
        "tags": ["blockers", "psychological-safety"],
        "opener": "You asked me for A/B testing tool access and year-end report guidance multiple times, and I dropped the ball. I want to fix both this week. What exactly do you need for each?"
      },
      {
        "topic": "Establish weekly meeting commitment",
        "tags": ["frequency", "meeting-structure"],
        "opener": "I'm committing to a weekly 30-minute one-on-one with you, same day and time every week. This is your meeting to drive. What day and time works best for you?"
      },
      {
        "topic": "Give specific, overdue recognition",
        "tags": ["feedback", "recognition"],
        "opener": "I want to call out some things I should have recognized months ago: the competitor research, the content calendar, the improved open rates from Q3 to Q4. That was strong work and you did it largely on your own. What are you most proud of from the last few months?"
      },
      {
        "topic": "Clarify and reprioritize current workload",
        "tags": ["workload", "expectations"],
        "opener": "Let's lay out everything on your plate right now and prioritize it together. What feels most urgent to you, and what feels unclear?"
      },
      {
        "topic": "Address team restructure anxiety",
        "tags": ["team-dynamics", "decision-making"],
        "opener": "I mentioned a possible restructure last time and left it vague. Let me share what I know so far, and then I want to hear your questions and concerns."
      },
      {
        "topic": "Open career growth conversation",
        "tags": ["career-growth", "energy-alignment"],
        "opener": "We've never really talked about where you want to go in your career. What drew you to marketing analytics, and what does your ideal trajectory look like?"
      },
      {
        "topic": "Set the new meeting format going forward",
        "tags": ["meeting-structure", "autonomy"],
        "opener": "Going forward, I want you to own the agenda for our 1-on-1s. Bring whatever's most on your mind — could be work, could be career stuff, could be frustrations. I'll add items too, but you go first. Let's also end each meeting with clear action items for both of us."
      }
    ],
    "phased_plan": [
      {
        "meeting": "Meeting 1 — Repair and Reset",
        "theme": "Acknowledge failures, rebuild trust, establish new cadence",
        "topics": [
          "Apologize for meeting gaps, dropped blockers, and lack of feedback — be specific about what you're sorry for",
          "Ask Riley how they're genuinely feeling about their experience so far — listen without defending",
          "Commit to weekly meetings and ask Riley to own the agenda",
          "Give specific, overdue recognition for competitor research, content calendar, and open rate improvement",
          "Unblock A/B testing access immediately — agree on a delivery date this week"
        ]
      },
      {
        "meeting": "Meeting 2 — Clarity and Direction",
        "theme": "Set clear expectations, prioritize work, establish feedback rhythm",
        "topics": [
          "Review and reprioritize Riley's current projects together — create a clear stack rank",
          "Close out the holiday campaign goal with a retrospective: what worked, what didn't, lessons learned",
          "Reset the email open rate goal with realistic targets, specific tools, and check-in milestones",
          "Address the team restructure with whatever information is available — be transparent about unknowns",
          "Give feedback on a recent piece of Riley's work using the SBI framework — model the new norm"
        ]
      },
      {
        "meeting": "Meeting 3 — Growth and Connection",
        "theme": "Career development, team connections, and sustainable pacing",
        "topics": [
          "Have the first real career conversation: aspirations, interests, ideal work mix, development goals",
          "Create a development goal around A/B testing proficiency — identify resources and a timeline",
          "Discuss team connections: who should Riley be collaborating with? Introduce peer relationships",
          "Check in on workload sustainability and whether the new meeting cadence is helping",
          "Ask Riley for feedback on your management: 'What's one thing I could do differently that would help you most?'"
        ]
      }
    ]
  }
}
